import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Avatar,
  IconButton,
  Divider,
  Stack,
  useTheme,
  useMediaQuery
} from "@mui/material";
import ConfettiExplosion from "react-confetti-explosion";
import { 
  IconCheck, 
  IconPhone, 
  IconBrandWhatsapp, 
  IconX, 
  IconMapPin, 
  IconUser,
  IconCircleCheckFilled
} from "@tabler/icons-react";
import { colors, fonts } from "../ColorFont/color";
import { url } from "../ConstUrl/Url";

const ContactPopup = ({ open, onClose, plot }) => {
  const [explode, setExplode] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const farmer = plot?.user;
  const contactNumber = plot?.contactNumber;

  useEffect(() => {
    if (open) {
      setExplode(true);
      const timer = setTimeout(() => setExplode(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!open) return null;

  const farmerName = farmer?.firstName ? `${farmer.firstName} ${farmer.lastName || ''}`.trim() : (farmer?.fullName || farmer?.name || "Farmer");
  const farmerLocation = [farmer?.tahsil || farmer?.district, farmer?.state].filter(Boolean).join(", ");
  const profilePhoto = farmer?.profilePhoto ? (farmer.profilePhoto.startsWith('http') ? farmer.profilePhoto : `${url}/${farmer.profilePhoto}`) : null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          position: 'relative'
        }
      }}
    >
      {explode && (
        <Box sx={{ position: "fixed", top: 0, left: "50%", transform: "translateX(-50%)", zIndex: 9999 }}>
          <ConfettiExplosion particleCount={150} width={1600} />
        </Box>
      )}

      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          color: '#919EAB',
          '&:hover': { color: '#1C252E', bgcolor: '#F4F6F8' }
        }}
      >
        <IconX size={20} />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {/* Header Visual */}
        <Box sx={{ 
          height: '100px', 
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary_dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          <Box sx={{ 
            width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center' 
          }}>
            <IconCircleCheckFilled size={32} color="#FFFFFF" stroke={2} />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ mt: -6, px: 3, pb: 4, textAlign: 'center' }}>
          <Avatar
            src={profilePhoto}
            sx={{
              width: 90,
              height: 90,
              mx: 'auto',
              border: '4px solid #FFFFFF',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
              bgcolor: colors.primary_light,
              fontSize: '32px',
              fontWeight: 700
            }}
          >
            {!profilePhoto && farmerName.charAt(0)}
          </Avatar>

          <Typography sx={{ 
            mt: 2, 
            fontFamily: fonts.poppins, 
            fontWeight: 800, 
            fontSize: '22px', 
            color: '#1C252E' 
          }}>
            {farmerName}
          </Typography>
          
          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
            <IconMapPin size={16} color="#637381" />
            <Typography sx={{ 
              fontFamily: fonts.poppins, 
              fontSize: '14px', 
              color: '#637381',
              fontWeight: 500
            }}>
              {farmerLocation || "Location not specified"}
            </Typography>
          </Stack>

          <Box sx={{ 
            mt: 3, p: 2, 
            bgcolor: '#F4F6F8', 
            borderRadius: '16px',
            border: '1px solid #E0E5EB'
          }}>
            <Typography sx={{ 
              fontFamily: fonts.poppins, 
              fontSize: '12px', 
              color: '#637381', 
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 700,
              mb: 0.5
            }}>
              Farmer's Contact Number
            </Typography>
            <Typography sx={{ 
              fontFamily: fonts.poppins, 
              fontSize: '20px', 
              fontWeight: 700, 
              color: colors.primary_dark 
            }}>
              {contactNumber || "Private"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<IconPhone size={20} />}
              onClick={() => window.location.href = `tel:${contactNumber}`}
              sx={{
                height: '52px',
                borderRadius: '14px',
                bgcolor: colors.primary,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '15px',
                boxShadow: `0px 8px 16px ${colors.primary_rgba_24}`,
                '&:hover': { bgcolor: colors.primary_dark }
              }}
            >
              Call Farmer
            </Button>
            <Button
              fullWidth
              variant="contained"
              startIcon={<IconBrandWhatsapp size={20} />}
              onClick={() => window.open(`https://wa.me/${contactNumber}`, '_blank')}
              sx={{
                height: '52px',
                borderRadius: '14px',
                bgcolor: '#25D366',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '15px',
                boxShadow: '0px 8px 16px rgba(37, 211, 102, 0.24)',
                '&:hover': { bgcolor: '#128C7E' }
              }}
            >
              WhatsApp
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactPopup;
